import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { purchasesEnabled } from "@/lib/billing/purchaseGate";
import { useChatContext } from "@/contexts/ChatContext";
import { useDualCredits } from "@/hooks/useDualCredits";
import { Send, Trash2, Maximize2, Minimize2, Coins, User } from "lucide-react";
import { cn } from "@/lib/utils";
import atlasLogo from "@/assets/atlas-assistant-logo.png";
import type { Message } from "@/contexts/ChatContext";

const GENERIC_SUGGESTIONS = [
  "Summarize my load board activity today.",
  "What changed on my board today?",
  "Which posted loads may need attention?",
];

const LOAD_SUGGESTIONS = [
  "Find me carriers running this lane?",
  "Show me market trends affecting my posted loads",
];

interface AtlasAssistantPanelProps {
  hasLoads?: boolean;
}

// Simple markdown-ish formatting for Atlas responses
const FormatMessage = ({ content }: { content: string }) => {
  const lines = content.split("\n");
  return (
    <>
      {lines.map((line, i) => {
        const trimmed = line.trim();
        if (trimmed.startsWith("### "))
          return <h4 key={i} className="text-xs font-bold mt-2 mb-0.5">{trimmed.slice(4)}</h4>;
        if (trimmed.startsWith("## "))
          return <h3 key={i} className="text-sm font-bold mt-2 mb-0.5">{trimmed.slice(3)}</h3>;
        if (trimmed.startsWith("# "))
          return <h2 key={i} className="text-sm font-bold mt-2 mb-0.5">{trimmed.slice(2)}</h2>;
        if (trimmed === "---" || trimmed === "***")
          return <hr key={i} className="my-1.5 border-t border-border/40" />;
        if (trimmed.startsWith("- ") || trimmed.startsWith("• ")) {
          const text = trimmed.slice(2);
          return (
            <div key={i} className="flex items-start gap-1.5 ml-1 my-0.5">
              <span className="text-muted-foreground mt-px text-[10px]">•</span>
              <span className="flex-1"><BoldText text={text} /></span>
            </div>
          );
        }
        if (trimmed === "") return <div key={i} className="h-1" />;
        return <div key={i} className={i > 0 ? "mt-0.5" : ""}><BoldText text={line} /></div>;
      })}
    </>
  );
};

const BoldText = ({ text }: { text: string }) => {
  const parts = text.split(/(\*\*.+?\*\*)/g);
  return (
    <>
      {parts.map((p, i) =>
        p.startsWith("**") && p.endsWith("**") && p.length > 4
          ? <strong key={i} className="font-semibold">{p.slice(2, -2)}</strong>
          : p ? <span key={i}>{p}</span> : null
      )}
    </>
  );
};

export function AtlasAssistantPanel({ hasLoads = false }: AtlasAssistantPanelProps) {
  const { atlasConversation, sendAtlasMessage, openAtlasChat, atlasCreditsExhausted } = useChatContext();
  const { companyBalance, userBalance, totalAvailable, loading: creditsLoading } = useDualCredits();
  const navigate = useNavigate();
  const [inputValue, setInputValue] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const isInitialRender = useRef(true);
  const prevMessageCount = useRef(atlasConversation?.messages?.length ?? 0);

  const balance = totalAvailable;
  const isOutOfCredits = atlasCreditsExhausted || (!creditsLoading && totalAvailable === 0);

  // Initialize Atlas conversation on mount
  useEffect(() => {
    openAtlasChat();
  }, []);

  // Escape key to collapse
  useEffect(() => {
    if (!isExpanded) return;
    const handler = (e: KeyboardEvent) => { if (e.key === "Escape") setIsExpanded(false); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [isExpanded]);

  // Auto-scroll on new messages
  useEffect(() => {
    const currentCount = atlasConversation?.messages?.length ?? 0;
    if (isInitialRender.current) {
      isInitialRender.current = false;
      prevMessageCount.current = currentCount;
      return;
    }
    if (currentCount > prevMessageCount.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
    prevMessageCount.current = currentCount;
  }, [atlasConversation?.messages]);

  const handleSend = (text: string) => {
    const trimmed = text.trim();
    if (!trimmed || isSending || isOutOfCredits) return;
    setIsSending(true);
    sendAtlasMessage(trimmed);
    setInputValue("");
    setTimeout(() => setIsSending(false), 1500);
  };

  const handleClearChat = () => {
    const userId = atlasConversation?.id?.replace("atlas-", "");
    if (userId) {
      localStorage.removeItem(`atlas-conversation-${userId}`);
      openAtlasChat();
      window.location.reload();
    }
  };

  const suggestions = [
    ...GENERIC_SUGGESTIONS,
    ...(hasLoads ? LOAD_SUGGESTIONS : []),
  ];

  const messages = atlasConversation?.messages ?? [];
  const nonLoadingMessages = messages.filter(m => !/^[🤔📊🔍💡]/.test(m.content));
  const hasConversation = nonLoadingMessages.length > 1;

  const panelContent = (
    <div id="atlas-assistant-panel" className={cn(
      "flex flex-col rounded-lg border bg-card overflow-hidden",
      isExpanded ? "w-full max-w-2xl max-h-[85vh]" : "h-full"
    )} style={{ boxShadow: "var(--shadow-sm)" }}>
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2.5 border-b flex-shrink-0" style={{ backgroundColor: "#02ffd3", borderColor: "hsl(var(--brand-teal-deep) / 0.25)" }}>
        <div className="flex-1 min-w-0 pr-3">
          <div className="flex items-center gap-2">
            <h4 className="text-[12px] font-bold text-foreground">Atlas AI Assistant</h4>
            {!creditsLoading && (
              <span className="inline-flex items-center gap-1.5 rounded-md bg-background/60 px-1.5 py-0.5 text-[10px] font-semibold text-foreground/80">
                <Coins className="h-3 w-3" />
                {companyBalance.toLocaleString()}
                <span className="text-foreground/40">·</span>
                <User className="h-2.5 w-2.5" />
                {userBalance.toLocaleString()}
              </span>
            )}
          </div>
          <p className="text-[11px] mt-0.5 leading-snug text-foreground/75">
            Ask anything about your loads, lanes, or market.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {hasConversation && (
            <button
              onClick={handleClearChat}
              className="p-1 rounded hover:bg-black/10 transition-colors"
              title="Clear conversation"
            >
              <Trash2 className="h-3.5 w-3.5 text-foreground/60" />
            </button>
          )}
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="p-1 rounded hover:bg-black/10 transition-colors"
            title={isExpanded ? "Collapse" : "Expand"}
          >
            {isExpanded
              ? <Minimize2 className="h-3.5 w-3.5 text-foreground/60" />
              : <Maximize2 className="h-3.5 w-3.5 text-foreground/60" />
            }
          </button>
          <img src={atlasLogo} alt="ATLAS AI" className="h-12 w-12 rounded-full object-contain" />
        </div>
      </div>

      {/* Messages area */}
      <div className={cn(
        "flex-1 overflow-y-auto px-3 py-2 space-y-2 min-h-0",
      )} style={isExpanded ? undefined : { maxHeight: "320px" }}>
        {/* Out-of-credits banner */}
        {isOutOfCredits && (
          <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-center">
            <p className="text-[11px] font-semibold text-destructive mb-1.5">ATLAS credits exhausted</p>
            <button
              type="button"
              onClick={() => navigate("/company-settings")}
              className="text-[11px] font-medium underline text-primary hover:text-primary/80"
            >
              Purchase credits to continue →
            </button>
          </div>
        )}

        {/* Show suggestions when conversation is empty/light and has credits */}
        {!hasConversation && !isOutOfCredits && (
          <div className="flex flex-col gap-1.5 py-1">
            {suggestions.map((q, i) => (
              <button
                key={q}
                type="button"
                disabled={isSending}
                onClick={() => handleSend(q)}
                className="flex items-center gap-2 rounded-md px-3 py-2 text-[11px] font-medium text-left transition-colors hover:bg-muted/10 disabled:opacity-50 disabled:pointer-events-none"
                style={{ backgroundColor: "hsl(0 0% 100%)", color: "hsl(215 50% 10%)", border: "1px solid hsl(var(--brand-teal) / 0.4)" }}
              >
                <span className="flex-shrink-0 text-[10px]" style={{ color: "hsl(var(--brand-teal-deep))" }}>
                  {i === 0 ? "◄" : i === 1 ? "◆" : "●"}
                </span>
                {q}
              </button>
            ))}
          </div>
        )}

        {/* Render messages */}
        {messages.map((message) => {
          const isLoading = /^[🤔📊🔍💡]/.test(message.content);

          if (isLoading) {
            return (
              <div key={message.id} className="flex justify-start">
                <div className="max-w-[85%] px-3 py-2 rounded-lg bg-muted text-[11px]">
                  <div className="flex items-center gap-2">
                    <div className="flex gap-0.5">
                      <div className="w-1.5 h-1.5 bg-muted-foreground/50 rounded-full animate-bounce" style={{ animationDelay: "0ms", animationDuration: "1s" }} />
                      <div className="w-1.5 h-1.5 bg-muted-foreground/50 rounded-full animate-bounce" style={{ animationDelay: "200ms", animationDuration: "1s" }} />
                      <div className="w-1.5 h-1.5 bg-muted-foreground/50 rounded-full animate-bounce" style={{ animationDelay: "400ms", animationDuration: "1s" }} />
                    </div>
                    <span className="text-muted-foreground">{message.content}</span>
                  </div>
                </div>
              </div>
            );
          }

          return (
            <div key={message.id} className={cn("flex", message.isMe ? "justify-end" : "justify-start")}>
              <div className={cn(
                "max-w-[85%] px-3 py-2 rounded-lg text-[11px] leading-relaxed",
                message.isMe
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted"
              )}>
                {message.isMe ? (
                  <span className="break-words">{message.content}</span>
                ) : (
                  <div className="break-words">
                    <FormatMessage content={message.content} />
                  </div>
                )}
              </div>
            </div>
          );
        })}

        {/* Collapsed suggestions when conversation is active and has credits */}
        {hasConversation && !isOutOfCredits && (
          <div className="flex flex-wrap gap-1 pt-1">
            {suggestions.slice(0, 3).map((q) => (
              <button
                key={q}
                type="button"
                disabled={isSending}
                onClick={() => handleSend(q)}
                className="text-[9px] px-2 py-1 rounded-full border transition-colors hover:bg-muted/50 disabled:opacity-50 text-muted-foreground"
                style={{ borderColor: "hsl(var(--brand-teal) / 0.3)" }}
              >
                {q.length > 30 ? q.slice(0, 28) + "…" : q}
              </button>
            ))}
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Real input */}
      <form
        onSubmit={(e) => { e.preventDefault(); handleSend(inputValue); }}
        className="flex items-center gap-2 px-3 py-2.5 flex-shrink-0"
        style={{ backgroundColor: "hsl(0 0% 100%)", borderTop: "1px solid hsl(var(--brand-teal-deep) / 0.2)" }}
      >
        <div className="flex-1 flex flex-col">
          <input
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            placeholder={isOutOfCredits ? "Credits exhausted" : "Ask Atlas anything…"}
            disabled={isSending || isOutOfCredits}
            className="flex-1 text-[11px] bg-transparent outline-none placeholder:text-muted-foreground text-foreground disabled:opacity-50"
          />
          {isOutOfCredits && !purchasesEnabled() ? (
            <span className="text-[9px] text-muted-foreground/60 mt-0.5">Credits exhausted</span>
          ) : isOutOfCredits ? (
            <button
              type="button"
              onClick={() => navigate("/company-settings?tab=credits")}
              className="text-[9px] font-semibold text-primary hover:underline mt-0.5"
            >
              Buy Credits →
            </button>
          ) : (
            <span className="text-[9px] text-muted-foreground/60 mt-0.5">1 credit per query</span>
          )}
        </div>
        <button
          type="submit"
          disabled={!inputValue.trim() || isSending || isOutOfCredits}
          className="flex h-7 w-7 items-center justify-center rounded-md disabled:opacity-40 transition-opacity"
          style={{ backgroundColor: "hsl(215 50% 12%)", color: "#02ffd3" }}
        >
          <Send className="h-3 w-3" />
        </button>
      </form>
    </div>
  );

  if (isExpanded) {
    return createPortal(
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
        onClick={(e) => { if (e.target === e.currentTarget) setIsExpanded(false); }}
      >
        {panelContent}
      </div>,
      document.body
    );
  }

  return panelContent;
}
